/* Shared entry/exit cascades; existing controls retain
   ownership of open/close state, focus, heights, rulers, and accessibility. */
(() => {
  if (window.mwyCascadeReady) return;
  window.mwyCascadeReady = true;
  const selector = '.collapsible-content, .mwy-faq__category, .store-menu-mobile .dropdown-menu, .mwy-takeover-submenu, .mwy-desktop-home-nav__dropdown, .site-nav__dropdown, [data-disclosure-list], [data-mwy-cascade]';
  const states = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let queued = false;
  const replaying = new WeakSet();
  const cssTime = (el, name, fallback) => {
    const raw = getComputedStyle(el).getPropertyValue(name).trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value * (raw.endsWith('s') && !raw.endsWith('ms') ? 1000 : 1) : fallback;
  };
  window.mwyCascadeShellTime = (el, closing = false) => reduced.matches ? 0 : cssTime(el, closing ? '--mwy-cascade-shell-close' : '--mwy-cascade-shell-open', closing ? 650 : 800);

  function measureMenu(el) {
    if (el.matches('.store-menu-mobile .dropdown-menu')) {
      el.style.setProperty('--mwy-menu-height', el.scrollHeight + 'px');
    }
  }

  function timing(el) {
    const css = getComputedStyle(el);
    const read = (name, fallback, time = false) => {
      const raw = css.getPropertyValue('--mwy-cascade-' + name).trim();
      const value = parseFloat(raw);
      return Number.isFinite(value) ? value * (time && raw.endsWith('s') && !raw.endsWith('ms') ? 1000 : 1) : fallback;
    };
    return { enabled: !reduced.matches && !!Element.prototype.animate && css.getPropertyValue('--mwy-cascade-enabled').trim() !== '0',
      duration: read('duration', 800, true), step: read('stagger', 110, true), span: read('window', 600, true),
      distance: read('distance', 8), closeRatio: read('close-percent', 40) / 100 };
  }

  function cancelExit(el, state) {
    state.serial = (state.serial || 0) + 1;
    state.closing = false;
    state.skipClose = false;
    el.removeAttribute('data-mwy-cascade-closing');
    el.removeAttribute('data-mwy-cascade-opening');
    stop(state);
  }

  function exit(el, state, commit) {
    const config = timing(el);
    if (!config.enabled) { stop(state); if (commit) commit(); return; }
    const children = items(el);
    // Capture an interrupted entry's actual appearance before cancelling it.
    const starts = children.map(child => ({ opacity: getComputedStyle(child).opacity, translate: getComputedStyle(child).translate }));
    stop(state);
    const serial = state.serial = (state.serial || 0) + 1;
    state.closing = true;
    el.setAttribute('data-mwy-cascade-closing', 'items');
    // Disclosures retract with their contents; navigation keeps its staged close.
    const concurrent = el.matches('.collapsible-content, .mwy-faq__category, .mwy-desktop-home-nav__dropdown, .site-nav__dropdown, .store-menu-mobile .dropdown-menu');
    const shellTime = window.mwyCascadeShellTime(el, true);
    const baseStep = Math.min(config.step * config.closeRatio, config.span * config.closeRatio / Math.max(1, children.length - 1));
    const baseDuration = config.duration * config.closeRatio;
    const scale = concurrent ? shellTime / Math.max(1, baseDuration + baseStep * Math.max(0, children.length - 1)) : 1;
    const step = baseStep * scale;
    if (el.matches('.mwy-desktop-home-nav__dropdown')) {
      state.animations.push(el.animate([
        {clipPath:'inset(0 0 0 0)'}, {clipPath:'inset(0 0 100% 0)'}
      ], {duration:shellTime,easing:'ease-in-out',fill:'forwards'}));
    }
    children.forEach((child, index) => {
      state.animations.push(child.animate([
        starts[index], { opacity: 0, translate: `0 -${config.distance}px` }
      ], { duration: baseDuration * scale, delay: (children.length - 1 - index) * step, easing: 'ease-in-out', fill: 'forwards' }));
    });
    if (concurrent) {
      el.setAttribute('data-mwy-cascade-closing', 'shell');
      state.skipClose = !!commit;
      if (commit) commit();
      // The original control owns height, rulers, focus and ARIA state.
    }
    Promise.all(state.animations.map(animation => animation.finished.catch(() => {}))).then(() => {
      if (state.serial !== serial) return;
      state.closing = false;
      el.setAttribute('data-mwy-cascade-closing', 'shell');
      if (!concurrent) {
        state.skipClose = !!commit;
        if (commit) commit();
      }
      // Keep items faded while the original control retracts the empty shell.
      state.open = isOpen(el);
      setTimeout(() => {
        if (state.serial !== serial) return;
        el.removeAttribute('data-mwy-cascade-closing');
        stop(state);
        schedule();
      }, concurrent ? 60 : shellTime + 60);
    });
  }

  function closeTarget(trigger) {
    if (trigger.matches('summary')) {
      const details = trigger.parentElement;
      return details.matches('.mwy-faq__category') ? details : details.querySelector('.site-nav__dropdown');
    }
    const id = trigger.getAttribute('aria-controls') || trigger.dataset.controls;
    const target = id && document.getElementById(id);
    return target && target.matches(selector) ? target : null;
  }

  function isOpen(el) {
    if (el.hidden || el.closest('[hidden], [inert]')) return false;
    if (el.matches('details')) return el.open;
    if (el.matches('.collapsible-content, .dropdown-menu')) return el.classList.contains('is-open');
    if (el.matches('.mwy-takeover-submenu')) return el.classList.contains('is-expanded');
    if (el.matches('[data-disclosure-list]')) return el.classList.contains('disclosure-list--visible');
    if (el.matches('[data-mwy-cascade]')) return el.getAttribute('data-mwy-cascade') === 'open';
    const hoverMenu = el.closest('.mwy-desktop-home-nav__details');
    if (hoverMenu) return hoverMenu.matches(':hover, :focus-within');
    const nativeMenu = el.closest('.site-nav__details');
    if (nativeMenu) return nativeMenu.open;
    const legacyMenu = el.closest('.site-nav--has-dropdown');
    if (legacyMenu) return legacyMenu.matches(':hover, :focus-within, .is-focused');
    const style = getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.pointerEvents !== 'none';
  }

  function items(el) {
    const root = el.matches('.mwy-faq__category') ? el.querySelector('.mwy-faq__category-body')
      : el.matches('.collapsible-content') ? el.querySelector('.collapsible-content__inner') : el;
    if (!root) return [];
    function collect(parent, depth = 0) {
      return [...parent.children].flatMap(child => {
        if (child.matches('script, style, template, br, input[type="hidden"], .hidden-label, [hidden]')) return [];
        // Keep rich text, charts, field groups, and nested disclosures intact.
        if (child.matches('p, li, a, button, label, input, textarea, table, figure, h1, h2, h3, h4, .product-block, .grid__item, .mwy-feedback, details')) return [child];
        if (child.matches(selector)) return []; // its own controller handles opening
        return depth < 4 && child.children.length ? collect(child, depth + 1) : [child];
      });
    }
    return collect(root).filter(child => getComputedStyle(child).display !== 'none');
  }

  function stop(state) {
    state.animations.forEach(animation => animation.cancel());
    state.animations = [];
  }

  function enter(el, state) {
    cancelExit(el, state);
    measureMenu(el);
    const children = items(el);
    children.forEach(child => child.setAttribute('data-mwy-cascade-item', ''));
    const openingDisclosure = el.matches('.collapsible-content');
    const openingSerial = state.serial;
    if (openingDisclosure) {
      // The theme releases overflow as soon as is-open is applied. Keep the
      // answer clipped to the animated shell until its height finishes, just
      // as the closing path already does naturally.
      el.setAttribute('data-mwy-cascade-opening', '');
      setTimeout(() => {
        if (state.serial === openingSerial) el.removeAttribute('data-mwy-cascade-opening');
      }, window.mwyCascadeShellTime(el) + 40);
    }
    if (reduced.matches || !Element.prototype.animate) return;
    const style = getComputedStyle(el);
    if (style.getPropertyValue('--mwy-cascade-enabled').trim() === '0') return;
    const number = (name, fallback) => {
      const value = parseFloat(style.getPropertyValue(name));
      return Number.isFinite(value) ? value : fallback;
    };
    // Shopify can minify 550ms into .55s, including custom properties.
    const milliseconds = (name, fallback) => {
      const value = style.getPropertyValue(name).trim();
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed * (value.endsWith('ms') ? 1 : value.endsWith('s') ? 1000 : 1) : fallback;
    };
    let duration = milliseconds('--mwy-cascade-duration', 550);
    const step = milliseconds('--mwy-cascade-stagger', 80);
    const windowMs = milliseconds('--mwy-cascade-window', 400);
    const initialDelay = milliseconds('--mwy-cascade-delay', 160);
    const distance = number('--mwy-cascade-distance', 8);
    const easing = style.getPropertyValue('--mwy-cascade-easing').trim() || 'ease-out';
    let stagger = Math.min(step, windowMs / Math.max(1, children.length - 1));
    let delay = Math.max(initialDelay, window.mwyCascadeShellTime(el) + milliseconds('--mwy-cascade-finish-lag', 450) - duration - stagger * Math.max(0, children.length - 1));
    // Answers unfold within the same timing window as their opening panel:
    // they do not pop in ahead of it or wait behind an empty shell. Navigation
    // and outer FAQ categories retain their existing timing rules.
    if (el.matches('.collapsible-content')) {
      const shellTime = window.mwyCascadeShellTime(el);
      duration = shellTime * 0.72;
      stagger = Math.min(shellTime * 0.08, shellTime * 0.18 / Math.max(1, children.length - 1));
      delay = shellTime * 0.08;
    } else if (el.matches('.mwy-faq__category')) {
      const span = duration + stagger * Math.max(0, children.length - 1);
      const scale = Math.min(1, window.mwyCascadeShellTime(el) / Math.max(1, span));
      duration *= scale;
      stagger *= scale;
      delay = 0;
    }
    children.forEach((child, index) => {
      const animation = child.animate([
        { opacity: 0, translate: `0 -${distance}px` },
        { opacity: 1, translate: '0 0' }
      ], { duration, delay: delay + index * stagger, easing, fill: 'backwards' });
      state.animations.push(animation);
    });
  }

  function sync() {
    queued = false;
    states.forEach((state, el) => {
      if (!el.isConnected) { stop(state); states.delete(el); }
    });
    document.querySelectorAll(selector).forEach(el => {
      const open = isOpen(el);
      let state = states.get(el);
      if (!state) {
        measureMenu(el);
        // Take ownership while closed, before the first opening can start the
        // legacy CSS cascade. Otherwise that transition starts, then restarts
        // when enter() adds our item marker on the following frame.
        items(el).forEach(child => child.setAttribute('data-mwy-cascade-item', ''));
        // Never replay on page load, scrolling, or resizing an already-open panel.
        state = { open, animations: [] };
        states.set(el, state);
        return;
      }
      if (open === state.open) return;
      state.open = open;
      if (open) enter(el, state);
      else if (state.skipClose) state.skipClose = false;
      else if (!state.closing) exit(el, state);
    });
  }
  function schedule() {
    if (!queued) { queued = true; requestAnimationFrame(sync); }
  }
  function init() {
    sync();
    // Let controls perform their own close only after the exit cascade. This
    // covers pointer and keyboard clicks without replacing their semantics.
    document.addEventListener('click', event => {
      const trigger = event.target.closest?.('summary, button[aria-controls], button[data-controls]');
      if (!trigger || replaying.has(trigger)) return;
      const target = closeTarget(trigger);
      const state = target && states.get(target);
      if (state && target.getAttribute('data-mwy-cascade-closing') === 'shell') {
        cancelExit(target, state);
        return; // allow the original control to reverse its shell animation
      }
      if (!state || !isOpen(target) || !timing(target).enabled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (state.closing) { cancelExit(target, state); enter(target, state); return; }
      exit(target, state, () => {
        replaying.add(trigger);
        try { trigger.click(); } finally { replaying.delete(trigger); }
      });
    }, true);
    // Header/card classes change throughout a scroll. They cannot change a
    // disclosure's state and must not trigger a full-page style scan.
    new MutationObserver(records => {
      const relevant = records.some(record => {
        const target = record.target;
        if (record.type === 'childList') {
          return [...record.addedNodes, ...record.removedNodes].some(node =>
            node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector)));
        }
        if (target.matches(selector + ', .site-nav__details, .mwy-desktop-home-nav__details, .site-nav--has-dropdown')) return true;
        return ['hidden', 'inert'].includes(record.attributeName) && !!target.querySelector(selector);
      });
      if (relevant) schedule();
    }).observe(document.body, {
      subtree: true, childList: true, attributes: true,
      attributeFilter: ['class', 'open', 'hidden', 'inert', 'aria-expanded', 'data-mwy-cascade']
    });
    // Hover-only desktop menus have no DOM state mutation.
    ['pointerover', 'pointerout', 'focusin', 'focusout'].forEach(type => {
      document.addEventListener(type, event => {
        if (event.target.closest?.('.mwy-desktop-home-nav__details, .site-nav--has-dropdown, .site-nav__details')) schedule();
      });
    });
    document.addEventListener('toggle', schedule, true);
    document.addEventListener('shopify:section:load', schedule);
    reduced.addEventListener('change', () => { states.forEach(stop); schedule(); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
